import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Highlight } from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import { Document, Packer, Paragraph, TextRun, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, HeadingLevel, AlignmentType, WidthType } from 'docx';

import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { 
  Save, Download, MessageSquare, Plus, 
  Users, FileText,
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, Table as TableIcon,
  Undo, Redo, Palette
} from 'lucide-react';
import './WordViewer.css';

const MenuBar = ({ editor, onInsertTable, onInsertImage, onInsertLink }) => {
  if (!editor) return null;

  return (
    <div className="tiptap-menubar">
      {/* Headings */}
      <div className="menu-group">
        <select
          onChange={(e) => {
            const level = parseInt(e.target.value);
            if (level === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level }).run();
            }
          }}
          value={
            editor.isActive('heading', { level: 1 }) ? 1 :
            editor.isActive('heading', { level: 2 }) ? 2 :
            editor.isActive('heading', { level: 3 }) ? 3 : 0
          }
          className="heading-select"
        >
          <option value="0">Normal text</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
      </div>

      <div className="menu-divider" />

      {/* Font Family */}
      <div className="menu-group">
        <select
          onChange={(e) => {
            if (e.target.value === 'unset') {
              editor.chain().focus().unsetFontFamily().run();
            } else {
              editor.chain().focus().setFontFamily(e.target.value).run();
            }
          }}
          value={editor.getAttributes('textStyle').fontFamily || 'unset'}
          className="font-select"
          title="Font family"
        >
          <option value="unset">Default Font</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Georgia">Georgia</option>
          <option value="Verdana">Verdana</option>
          <option value="Comic Sans MS">Comic Sans MS</option>
          <option value="Impact">Impact</option>
          <option value="Trebuchet MS">Trebuchet MS</option>
          <option value="Palatino">Palatino</option>
          <option value="Garamond">Garamond</option>
          <option value="Bookman">Bookman</option>
          <option value="Tahoma">Tahoma</option>
        </select>
      </div>

      {/* Font Size */}
      <div className="menu-group">
        <select
          onChange={(e) => {
            if (e.target.value === 'unset') {
              editor.chain().focus().unsetFontSize().run();
            } else {
              editor.chain().focus().setFontSize(e.target.value).run();
            }
          }}
          value={editor.getAttributes('textStyle').fontSize || 'unset'}
          className="size-select"
          title="Font size"
        >
          <option value="unset">Default</option>
          <option value="8pt">8</option>
          <option value="9pt">9</option>
          <option value="10pt">10</option>
          <option value="11pt">11</option>
          <option value="12pt">12</option>
          <option value="14pt">14</option>
          <option value="16pt">16</option>
          <option value="18pt">18</option>
          <option value="20pt">20</option>
          <option value="24pt">24</option>
          <option value="28pt">28</option>
          <option value="32pt">32</option>
          <option value="36pt">36</option>
          <option value="48pt">48</option>
          <option value="72pt">72</option>
        </select>
      </div>

      <div className="menu-divider" />

      {/* Basic Formatting */}
      <div className="menu-group">
        <button
          onClick={() => {
            editor.chain().focus().toggleBold().run();
          }}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            editor.chain().focus().toggleItalic().run();
          }}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            editor.chain().focus().toggleUnderline().run();
          }}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            editor.chain().focus().toggleStrike().run();
          }}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            editor.chain().focus().toggleHighlight().run();
          }}
          className={editor.isActive('highlight') ? 'is-active' : ''}
          title="Highlight"
        >
          <Palette className="h-4 w-4" />
        </button>
      </div>

      <div className="menu-divider" />

      {/* Text Color */}
      <div className="menu-group">
        <input
          type="color"
          onInput={(e) => {
            editor.chain().focus().setColor(e.target.value).run();
          }}
          value={editor.getAttributes('textStyle').color || '#000000'}
          className="color-picker"
          title="Text Color"
        />
      </div>

      <div className="menu-divider" />

      {/* Alignment */}
      <div className="menu-group">
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
          title="Justify"
        >
          <AlignJustify className="h-4 w-4" />
        </button>
      </div>

      <div className="menu-divider" />

      {/* Lists */}
      <div className="menu-group">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
      </div>

      <div className="menu-divider" />

      {/* Insert */}
      <div className="menu-group">
        <button
          onClick={onInsertLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Insert Link"
        >
          <LinkIcon className="h-4 w-4" />
        </button>
        <button
          onClick={onInsertImage}
          title="Insert Image"
        >
          <ImageIcon className="h-4 w-4" />
        </button>
        <button
          onClick={onInsertTable}
          title="Insert Table"
        >
          <TableIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="menu-divider" />

      {/* Undo/Redo */}
      <div className="menu-group">
        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const WordViewerNew = ({ file, content, projectId, myProfile, onSave, onDownload, onClose, isLoading }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeViewers, setActiveViewers] = useState([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  
  const { toast } = useToast();
  const { t } = useLanguage();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: {
          depth: 100,
          newGroupDelay: 500,
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Highlight.configure({
        multicolor: false
      }),
      TextStyle,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSize,
      Color,
    ],
    content: content || '<p></p>',
    onUpdate: ({ editor }) => {
      setHasUnsavedChanges(true);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content && editor.getHTML() !== content) {
      editor.commands.setContent(content, false); // false = don't add to history
      setHasUnsavedChanges(false);
    }
  }, [content, editor]);

  // Simulate active viewers
  useEffect(() => {
    if (myProfile) {
      setActiveViewers([{
        id: myProfile.id,
        name: myProfile.name,
        color: '#3b82f6'
      }]);
    }
  }, [myProfile]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Close download menu on Escape
      if (e.key === 'Escape' && showDownloadMenu) {
        setShowDownloadMenu(false);
      }
    };

    const handleClickOutside = (e) => {
      if (showDownloadMenu && !e.target.closest('.relative')) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hasUnsavedChanges, showDownloadMenu]);

  // Load comments
  useEffect(() => {
    if (file && projectId && myProfile) {
      loadComments();
    }
  }, [file, projectId, myProfile]);

  const loadComments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/comments?user_id=${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  // Insert table
  const insertTable = () => {
    if (editor) {
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      toast({
        title: "Table Inserted",
        description: "A 3x3 table has been added.",
      });
    }
  };

  // Insert image
  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  // Insert link
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  // Save document
  const handleSave = async () => {
    if (!editor) return;
    
    const editorContent = editor.getHTML();
    
    if (!editorContent || editorContent === '<p></p>') {
      toast({
        title: "Cannot Save",
        description: "Document is empty.",
        variant: "destructive"
      });
      return;
    }

    if (!hasUnsavedChanges) {
      toast({
        title: "No Changes",
        description: "No changes to save.",
      });
      return;
    }

    try {
      await onSave(editorContent);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      
      toast({
        title: "✅ Saved",
        description: "Document saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Add comment
  const addComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: "Empty Comment",
        description: "Please enter a comment.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/comments?user_id=${myProfile.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newComment.trim(), line: 1 })
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment('');
        toast({
          title: "✅ Comment Added",
          description: "Your comment has been added.",
        });
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Delete comment
  const deleteComment = async (commentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/comments/${commentId}?user_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
        toast({ title: "✅ Deleted", description: "Comment removed." });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Get save status
  const getSaveStatus = () => {
    if (hasUnsavedChanges) {
      return <span className="text-orange-600 text-sm">● Unsaved changes</span>;
    }
    if (lastSaved) {
      const seconds = Math.floor((new Date() - lastSaved) / 1000);
      if (seconds < 60) return <span className="text-green-600 text-sm">✓ Saved just now</span>;
      if (seconds < 3600) return <span className="text-green-600 text-sm">✓ Saved {Math.floor(seconds / 60)}m ago</span>;
    }
    return <span className="text-gray-500 text-sm">Ready</span>;
  };

  // Custom download handlers for edited documents
  const handleDownloadAsWord = async () => {
    if (!editor) return;
    
    try {
      const htmlContent = editor.getHTML();
      
      // Parse HTML to extract formatted content
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const children = [];

      // Helper function to process inline formatting
      const processTextNode = (node, parentElement) => {
        const runs = [];
        const text = node.textContent || '';
        
        if (!text.trim()) return runs;

        // Check parent element for formatting
        let bold = false;
        let italic = false;
        let underline = false;
        let color = undefined;
        let highlight = undefined;
        
        let currentNode = parentElement;
        while (currentNode && currentNode !== doc.body) {
          const tagName = currentNode.tagName?.toLowerCase();
          const style = currentNode.getAttribute('style') || '';
          
          if (tagName === 'strong' || tagName === 'b') bold = true;
          if (tagName === 'em' || tagName === 'i') italic = true;
          if (tagName === 'u') underline = true;
          if (tagName === 'mark') highlight = 'yellow';
          
          // Extract color from style
          const colorMatch = style.match(/color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (colorMatch) {
            const [_, r, g, b] = colorMatch;
            color = ('0' + parseInt(r).toString(16)).slice(-2) + 
                   ('0' + parseInt(g).toString(16)).slice(-2) + 
                   ('0' + parseInt(b).toString(16)).slice(-2);
          }
          
          currentNode = currentNode.parentElement;
        }
        
        runs.push(new TextRun({ 
          text,
          bold,
          italic,
          underline: underline ? {} : undefined,
          color,
          highlight
        }));
        
        return runs;
      };

      // Process each top-level element
      Array.from(doc.body.children).forEach(element => {
        const tagName = element.tagName?.toLowerCase();
        
        if (tagName === 'p') {
          const runs = [];
          
          // Process all child nodes for formatting
          const processNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              runs.push(...processTextNode(node, node.parentElement));
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              node.childNodes.forEach(child => processNode(child));
            }
          };
          
          element.childNodes.forEach(node => processNode(node));
          
          if (runs.length > 0) {
            // Check paragraph alignment
            const style = element.getAttribute('style') || '';
            const alignMatch = style.match(/text-align:\s*(\w+)/);
            const alignment = alignMatch ? 
              (alignMatch[1] === 'center' ? AlignmentType.CENTER :
               alignMatch[1] === 'right' ? AlignmentType.RIGHT :
               alignMatch[1] === 'justify' ? AlignmentType.JUSTIFIED :
               AlignmentType.LEFT) : AlignmentType.LEFT;
            
            children.push(new Paragraph({ 
              children: runs,
              alignment,
              spacing: { after: 200 }
            }));
          }
        }
        else if (tagName === 'h1') {
          children.push(new Paragraph({
            text: element.textContent || '',
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 }
          }));
        }
        else if (tagName === 'h2') {
          children.push(new Paragraph({
            text: element.textContent || '',
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 250 }
          }));
        }
        else if (tagName === 'h3') {
          children.push(new Paragraph({
            text: element.textContent || '',
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 200 }
          }));
        }
        else if (tagName === 'ul') {
          element.querySelectorAll('li').forEach((li, index) => {
            children.push(new Paragraph({
              text: li.textContent || '',
              bullet: { level: 0 },
              spacing: { after: 100 }
            }));
          });
        }
        else if (tagName === 'ol') {
          element.querySelectorAll('li').forEach((li, index) => {
            children.push(new Paragraph({
              text: li.textContent || '',
              numbering: { reference: 'numbering', level: 0 },
              spacing: { after: 100 }
            }));
          });
        }
        else if (tagName === 'table') {
          try {
            const rows = [];
            element.querySelectorAll('tr').forEach(tr => {
              const cells = [];
              tr.querySelectorAll('td, th').forEach(cell => {
                cells.push(new DocxTableCell({
                  children: [new Paragraph({ 
                    text: cell.textContent || '',
                    spacing: { after: 0 }
                  })],
                  margins: {
                    top: 100,
                    bottom: 100,
                    left: 100,
                    right: 100
                  }
                }));
              });
              if (cells.length > 0) {
                rows.push(new DocxTableRow({ children: cells }));
              }
            });
            
            if (rows.length > 0) {
              children.push(new DocxTable({
                rows,
                width: { size: 100, type: WidthType.PERCENTAGE }
              }));
            }
          } catch (tableError) {
            console.error('Error processing table:', tableError);
            // Fallback to text if table fails
            children.push(new Paragraph({
              text: element.textContent || '',
              spacing: { after: 200 }
            }));
          }
        }
      });

      // Add title if no content
      if (children.length === 0) {
        children.push(new Paragraph({
          text: 'Document content',
          spacing: { after: 200 }
        }));
      }

      // Create Word document with numbering support
      const wordDoc = new Document({
        sections: [{
          properties: {},
          children
        }],
        numbering: {
          config: [{
            reference: 'numbering',
            levels: [{
              level: 0,
              format: 'decimal',
              text: '%1.',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 }
                }
              }
            }]
          }]
        }
      });

      // Generate and download
      const blob = await Packer.toBlob(wordDoc);
      const filename = file.original_filename.replace(/\.(docx?|html|txt)$/i, '') + '_edited.docx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "✅ Downloaded as Word",
        description: "Document saved as .docx with formatting preserved.",
      });
      setShowDownloadMenu(false);
    } catch (error) {
      console.error('Error creating Word document:', error);
      toast({
        title: "Download Failed",
        description: "Could not create Word document. Please try HTML format instead.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadAsHTML = () => {
    if (!editor) return;
    
    const htmlContent = editor.getHTML();
    const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${file.original_filename}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            max-width: 850px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
        }
        table { border-collapse: collapse; width: 100%; margin: 16px 0; }
        table td, table th { border: 1px solid #ddd; padding: 8px; }
        table th { background: #f3f4f6; font-weight: 600; }
        img { max-width: 100%; height: auto; }
    </style>
</head>
<body>
    ${htmlContent}
</body>
</html>`;
    
    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.original_filename.replace(/\.(docx?|txt)$/i, '.html');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded as HTML",
      description: "Document saved as HTML file that can be opened in any browser.",
    });
    setShowDownloadMenu(false);
  };

  const handleDownloadAsText = () => {
    if (!editor) return;
    
    const textContent = editor.getText();
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.original_filename.replace(/\.(docx?|html)$/i, '.txt');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded as Text",
      description: "Document saved as plain text file.",
    });
    setShowDownloadMenu(false);
  };

  const handleDownloadOriginal = () => {
    onDownload(); // Use the original download handler
    toast({
      title: "Note",
      description: "Downloading original file. Edits made in the editor are not included.",
    });
    setShowDownloadMenu(false);
  };

  const wordCount = editor ? editor.getText().split(/\s+/).filter(w => w.length > 0).length : 0;
  const charCount = editor ? editor.getText().length : 0;

  return (
    <div className="word-viewer-container">
      {/* Header */}
      <div className="docs-header">
        <div className="docs-header-left">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{file.original_filename}</h1>
              <div className="flex items-center gap-2">{getSaveStatus()}</div>
            </div>
          </div>
        </div>
        
        <div className="docs-header-center">
          <div className="flex items-center gap-2">
            {/* Active Viewers */}
            {activeViewers.length > 0 && (
              <div className="flex items-center gap-2 mr-4 px-3 py-1 bg-gray-100 rounded-full">
                <Users className="h-4 w-4 text-gray-600" />
                <div className="flex -space-x-2">
                  {activeViewers.map((viewer) => (
                    <div
                      key={viewer.id}
                      className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-medium text-white"
                      style={{ backgroundColor: viewer.color }}
                      title={viewer.name}
                    >
                      {viewer.name.charAt(0).toUpperCase()}
                    </div>
                  ))}
                </div>
                <span className="text-xs text-gray-600">{activeViewers.length} {t('viewing')}</span>
              </div>
            )}
            
            <Button 
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
              title="Save (Ctrl+S)"
            >
              <Save className="h-4 w-4 mr-1" />
              {isLoading ? 'Saving...' : t('save')}
            </Button>
            
            <div className="relative">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              >
                <Download className="h-4 w-4 mr-1" />
                {t('download')}
              </Button>
              
              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-2">
                    <button
                      onClick={handleDownloadAsWord}
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="font-medium text-sm text-blue-600">Download as Word (.docx)</div>
                        <div className="text-xs text-gray-500">With all edits & formatting</div>
                      </div>
                    </button>
                    <button
                      onClick={handleDownloadAsHTML}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="font-medium text-sm">Download as HTML</div>
                        <div className="text-xs text-gray-500">Open in any browser</div>
                      </div>
                    </button>
                    <button
                      onClick={handleDownloadAsText}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-gray-600" />
                      <div>
                        <div className="font-medium text-sm">Download as Text</div>
                        <div className="text-xs text-gray-500">Plain text only</div>
                      </div>
                    </button>
                    <button
                      onClick={handleDownloadOriginal}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-t"
                    >
                      <Download className="h-4 w-4 text-orange-600" />
                      <div>
                        <div className="font-medium text-sm">Original File</div>
                        <div className="text-xs text-gray-500">Without edits</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <Button variant="outline" size="sm" onClick={() => setShowComments(!showComments)}>
              <MessageSquare className="h-4 w-4 mr-1" />
              {t('comments')} ({comments.length})
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar - Fixed at top */}
      <div className="docs-toolbar-fixed">
        <MenuBar 
          editor={editor} 
          onInsertTable={insertTable}
          onInsertImage={insertImage}
          onInsertLink={insertLink}
        />
      </div>

      {/* Main Content */}
      <div className="docs-main-container">
        <div className={`docs-editor-wrapper ${showComments ? 'with-sidebar' : ''}`}>
          <div className="docs-editor-container">
            <EditorContent editor={editor} className="tiptap-editor" />
          </div>
        </div>

        {/* Comments Sidebar */}
        {showComments && (
          <div className="docs-comments-sidebar">
            <div className="comments-header">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {t('comments')} ({comments.length})
              </h3>
            </div>
            
            <div className="add-comment-section">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="text-sm resize-none"
                rows={3}
              />
              <Button 
                size="sm" 
                onClick={addComment}
                disabled={!newComment.trim()}
                className="mt-2 w-full bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Comment
              </Button>
            </div>

            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-author">{comment.author_name}</span>
                    <button onClick={() => deleteComment(comment.id)} className="comment-delete">✕</button>
                  </div>
                  <p className="comment-text">{comment.text}</p>
                  <span className="comment-time">{new Date(comment.timestamp).toLocaleString()}</span>
                </div>
              ))}
              
              {comments.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-8">
                  No comments yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="docs-status-bar">
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span>Words: {wordCount}</span>
          <span>Characters: {charCount}</span>
          {lastSaved && <span>Last saved: {lastSaved.toLocaleTimeString()}</span>}
        </div>
      </div>
    </div>
  );
};

export default WordViewerNew;
